import { normalizeForSearch } from "@/lib/search"
import type { MenuCategory, MenuItem } from "./types"

export type MenuFilter = {
  query: string
  categoryId: number | "all"
}

/**
 * Pure filter shared by the menu screen: category + name/description search that
 * ignores case and accents. A diner hunting for "jalapeño" types "jalapeno" —
 * the accent is a long-press away on a phone keyboard, so requiring it hid
 * dishes that were right there on the menu.
 */
export function filterMenuItems(items: MenuItem[], { query, categoryId }: MenuFilter): MenuItem[] {
  const normalizedQuery = normalizeForSearch(query.trim())

  return items.filter((item) => {
    const inCategory = categoryId === "all" || item.categoryId === categoryId
    const matchesQuery =
      normalizedQuery.length === 0 ||
      normalizeForSearch(item.name).includes(normalizedQuery) ||
      (item.description ? normalizeForSearch(item.description).includes(normalizedQuery) : false)

    return inCategory && matchesQuery
  })
}

export type MenuGroup = {
  id: number | "uncategorized"
  name: string
  emoji?: string | null
  items: MenuItem[]
}

/**
 * Splits the flat item list into one group per category, following the category
 * order the backend sends (creation order). Empty categories are dropped and
 * items without a known category fall into a trailing group.
 */
export function groupMenuItemsByCategory(
  items: MenuItem[],
  categories: MenuCategory[],
  uncategorizedLabel: string
): MenuGroup[] {
  const groups = new Map<number, MenuGroup>(
    categories.map((category) => [
      category.id,
      { id: category.id, name: category.name, emoji: category.emoji, items: [] },
    ])
  )
  const uncategorized: MenuItem[] = []

  for (const item of items) {
    const group = item.categoryId === null ? undefined : groups.get(item.categoryId)
    if (group) group.items.push(item)
    else uncategorized.push(item)
  }

  const result = [...groups.values()].filter((group) => group.items.length > 0)

  if (uncategorized.length > 0) {
    result.push({ id: "uncategorized", name: uncategorizedLabel, items: uncategorized })
  }

  return result
}
